// Import react elements
import { useState, useEffect } from "react";

// Import navigation hook
import { useNavigate } from "react-router-dom";

// Import hooks
import { useCustomizr } from "global/hooks/useCustomizr";
import { useToast } from "global/hooks/useToast";

// Import components
import CreateButton from "global/components/CreateButton";
import { ImageLoader } from "global/components/ImageLoader";

// Import utilities
import { S2B } from "global/utilities/BooleanConverters";



const PreviewPane = ({ image, updateSelectedImage, onDelete, onCustomizrSave }) => {

    // Get the navigate function for programmatic navigation
    const navigate = useNavigate();

    // Get the Customizr open function and ready flag.
    const { openCustomizr, isReady: isCustomizrReady } = useCustomizr();

    // Define toast options
    const { toastError } = useToast();

    // State to store / edit image details
    const [formData, setFormData] = useState({ name: "", type: "", category: "", subcategory: "" });



    // Update local state when image changes
    useEffect(() => {

        if (image) {

            setFormData({
                id: image.id || "",
                name: image.name || "",
                type: image.type || "",
                category: image.category || "",
                subcategory: image.subcategory || "",
            });

        }

    }, [image]);



    // Handle form input changes
    const handleChange = (event) => {

        // Set name and value from the input
        const { name, value } = event.target;

        // Update the formData on input change
        setFormData((prev) => ({ ...prev, [name]: value }));

    };



    // Save the editable bucket details
    const handleSave = () => {

        // Update this bucket item without exposing the internal locked state
        updateSelectedImage(image.id, formData);

    };



    // Handle edit in customizr
    const handleEditInCustomizr = async () => {

        try {
        
            // Open Customizr and wait for it to finish.
            await openCustomizr({
                type: "edit",
                imageData: image.image_url,
                save_image_to_bucket: "Y"
            }, async (data) => {

                // Keep a simple log of what Customizr sent back.
                console.log("Customizr finished with response:", data);

                // Refresh the image bucket without remounting the whole page.
                await onCustomizrSave?.(data);
            
            });

        } catch (error) {

            // Handle errors or user cancellation
            console.error("Customizr did not complete:", error);
            toastError({ title: "Error", message: error?.message || "Something went wrong while opening Customizr." });

        }

    };



    // If no image is selected display 'select an image' message
    if (!image) return <div className="preview-pane">Select an image</div>;

    // Only image-class bucket files can be edited in Customizr.
    const isImageFile = image.file_class === "image";

    // Non-image files use the real or placeholder thumbnail supplied by the API.
    let previewImageUrl = image.thumbnail_url || image.image_url;

    // Normal images should use their full-size image in the preview pane.
    if (isImageFile) { previewImageUrl = image.image_url; }



    return (
        
        <div className="preview-pane">

            <div className="preview-pane__image-container">

                {/* Show the real image or the file-class thumbnail supplied by the API. */}
                <ImageLoader src={previewImageUrl} alt={image.name} className="preview-pane__image" />

                {/* Show 'Edit Image' button for editable image files only. */}
                {isImageFile && !S2B(image.locked) && (image.locked !== "Y") &&

                    <CreateButton
                        tooltip={
                            // Show a loading message until Customizr is ready.
                            isCustomizrReady ? "Edit Image" : "Loading Customizr..."
                        }
                        title={
                            // Use a pencil unicode character.
                            "🖉"
                        }
                        size="xs"
                        onClick={handleEditInCustomizr}
                        className="preview-pane__image__edit-btn"
                        disabled={
                            // Do not let the user click before Customizr is ready.
                            !isCustomizrReady
                        }
                    />

                }

                {/* Non-image files can be opened directly but cannot be edited in Customizr. */}
                {!isImageFile &&

                    <div className="preview-pane__actions horizontal-align-content-center">

                        <CreateButton
                            title="Open File"
                            size="s"
                            onClick={() => window.open(image.image_url, "_blank", "noopener,noreferrer")}
                            disabled={!image.image_url}
                        />

                    </div>

                }

            </div>

            <div className="preview-pane__form">
                
                <label>Name
                    <input name="name" value={formData.name} onChange={handleChange} disabled={S2B(image.locked)} />
                </label>

                <label>Type
                    <input name="type" value={formData.type} onChange={handleChange} disabled={S2B(image.locked)} />
                </label>

                <label>Category
                    <input name="category" value={formData.category} onChange={handleChange} disabled={S2B(image.locked)} />
                </label>

                <label>Subcategory
                    <input name="subcategory" value={formData.subcategory} onChange={handleChange} disabled={S2B(image.locked)} />
                </label>

                {/* Only internally protected bucket records remain read-only. */}
                {!S2B(image.locked) &&

                    <>

                        <div className="preview-pane__actions horizontal-align-content-center flex-wrap">

                            <CreateButton title="Save" size="s" onClick={handleSave} />

                            <CreateButton title="Delete" isDelete={true} size="s" onClick={onDelete} />

                        </div>

                        <br />

                        {isImageFile &&

                            <div>

                                <CreateButton
                                    title="Create POD Product"
                                    size="s"
                                    onClick={() => {

                                        // Open the normal POD design editor with this image already assigned
                                        navigate("/designs", {

                                            state: {

                                                createPodDesign: {

                                                    image_url: image.image_url,
                                                    title: image.name || "Untitled",

                                                },

                                            },

                                        });

                                    }}
                                />

                            </div>

                        }

                    </>

                }

            </div>

        </div>

    );

};

export default PreviewPane;
